import {
  type AfterViewInit,
  Component,
  inject,
  type OnDestroy,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { type CONSENT_ACTION_VALUES, ConsentService } from '@core/consent';
import {
  NgbModal,
  NgbModalModule,
  type NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { CookiewallComponent } from '@cookie-settings/ui';
import { CONSENT_PARAM } from '@core/jss-seo';
import { PlatformService } from '@core/platform';

import { CookiewallService } from '../../cookiewall.service';
import { ConsentAnalyticsService } from '../../consent-analytics.service';

@Component({
  selector: 'wl-cookiewall-container',
  standalone: true,
  template: ``,
  styles: [``],
  imports: [NgbModalModule],
})
export class CookiewallContainerComponent implements AfterViewInit, OnDestroy {
  readonly onDestroy$ = new Subject();

  readonly #modalService = inject(NgbModal);
  readonly #cookiewallService = inject(CookiewallService);
  readonly #consentService = inject(ConsentService);
  readonly #consentAnalytics = inject(ConsentAnalyticsService);
  readonly #router = inject(Router);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #platformService = inject(PlatformService);

  #modalRef: NgbModalRef | null = null;

  readonly #navigationEvents = this.#router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    filter(() => this.#platformService.isClient()),
    filter(() => this.#getCurrentQueryParams()[CONSENT_PARAM] === 'true'),
    filter(() => !this.#consentService.allowsTrackingCookies()),
    filter(() => !this.#modalRef),
    tap(() => this.#summonCookiewall())
  );

  async ngAfterViewInit() {
    if (
      this.#cookiewallService.isCookieConsentPromptRequired() &&
      !this.#modalRef
    ) {
      this.#summonCookiewall();
    }

    this.#listenForCookiewallDisplayConditions();
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  #summonCookiewall() {
    this.#modalRef = this.#modalService.open(CookiewallComponent, {
      beforeDismiss: () => false,
      scrollable: true,
      size: 'lg',
    });
    // Only send an impression beacon when we actually show a cookie wall.
    this.#consentAnalytics.sendImpressionBeacon();

    this.#modalRef.result.then((action: CONSENT_ACTION_VALUES) => {
      this.#cookiewallService.setConsentLevel(
        this.#consentService.consentLevelForAction(action)
      );
      this.#consentAnalytics.sendSubmissionBeacon(
        this.#consentService.consentLevelForAction(action)
      );
      this.#modalRef = null;
    });

    this.#modalRef.closed
      .pipe(
        tap(() => {
          const queryParams = { ...this.#getCurrentQueryParams() };

          delete queryParams[CONSENT_PARAM];
          this.#router.navigate([], {
            queryParams,
            replaceUrl: true,
            queryParamsHandling: '',
          });
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe();
  }

  #listenForCookiewallDisplayConditions() {
    this.#navigationEvents
      .pipe(
        filter(
          () =>
            this.#cookiewallService.isCookieConsentPromptRequired() &&
            !this.#modalRef
        ),
        tap(() => {
          this.#summonCookiewall();
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe();
  }

  #getCurrentQueryParams(): Record<string, string> {
    let route = this.#activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.snapshot.queryParams;
  }
}
